import React, { useState } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Plus, 
  Calendar, 
  Users, 
  MoreHorizontal,
  CheckCircle,
  PlayCircle,
  PauseCircle,
  AlertCircle,
  Clock,
  Target
} from 'lucide-react';
import { ProjectStatus } from '@/types';
import { CreateProjectDialog } from '@/components/CreateProjectDialog';

export const ProjectsList: React.FC = () => {
  const { projects, setCurrentProject, getProjectTasks } = useProject();
  const { user } = useAuth();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const getStatusIcon = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.COMPLETED: return <CheckCircle className="w-4 h-4 text-green-500" />;
      case ProjectStatus.IN_PROGRESS: return <PlayCircle className="w-4 h-4 text-blue-500" />;
      case ProjectStatus.ON_HOLD: return <PauseCircle className="w-4 h-4 text-yellow-500" />;
      case ProjectStatus.PLANNING: return <Target className="w-4 h-4 text-purple-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.COMPLETED: return 'bg-green-100 text-green-800';
      case ProjectStatus.IN_PROGRESS: return 'bg-blue-100 text-blue-800';
      case ProjectStatus.ON_HOLD: return 'bg-yellow-100 text-yellow-800';
      case ProjectStatus.PLANNING: return 'bg-purple-100 text-purple-800';
      case ProjectStatus.CANCELLED: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.COMPLETED: return 'Concluído';
      case ProjectStatus.IN_PROGRESS: return 'Em Andamento';
      case ProjectStatus.ON_HOLD: return 'Pausado';
      case ProjectStatus.PLANNING: return 'Planejamento';
      case ProjectStatus.CANCELLED: return 'Cancelado';
      default: return status;
    }
  };

  const getDaysRemaining = (endDate: Date) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const mockUsers = [
    { id: '1', name: 'Admin User', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face' },
    { id: '2', name: 'Manager User', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b865?w=32&h=32&fit=crop&crop=face' },
    { id: '3', name: 'Collaborator User', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face' }
  ];

  const getProjectMembers = (project: any) => {
    return project.members.map((member: any) => {
      const userData = mockUsers.find(u => u.id === member.userId);
      return userData ? { ...userData, role: member.role } : null;
    }).filter(Boolean);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Projetos</h1>
          <p className="text-gray-600 mt-1">Gerencie todos os seus projetos</p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-gradient-to-r from-blue-500 to-purple-600">
          <Plus className="w-4 h-4 mr-2" />
          Novo Projeto
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total</p>
                <p className="text-2xl font-bold text-blue-900">{projects.length}</p>
              </div>
              <Target className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Ativos</p>
                <p className="text-2xl font-bold text-green-900">
                  {projects.filter(p => p.status === ProjectStatus.IN_PROGRESS).length}
                </p>
              </div>
              <PlayCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Concluídos</p>
                <p className="text-2xl font-bold text-purple-900">
                  {projects.filter(p => p.status === ProjectStatus.COMPLETED).length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">Pausados</p>
                <p className="text-2xl font-bold text-yellow-900">
                  {projects.filter(p => p.status === ProjectStatus.ON_HOLD).length}
                </p>
              </div>
              <PauseCircle className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {projects.map(project => {
          const projectTasks = getProjectTasks(project.id);
          const completedTasks = projectTasks.filter(t => t.status === 'done').length;
          const daysRemaining = getDaysRemaining(project.endDate);
          const projectMembers = getProjectMembers(project);

          return (
            <Card key={project.id} className="hover:shadow-lg transition-shadow duration-200 cursor-pointer group">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(project.status)}
                    <Badge variant="secondary" className={getStatusColor(project.status)}>
                      {getStatusText(project.status)}
                    </Badge>
                  </div>
                  <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </div>
                <CardTitle className="text-lg font-semibold text-gray-900 mt-2">
                  {project.name}
                </CardTitle>
                <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Progress */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Progresso</span>
                    <span className="text-sm font-bold text-gray-900">{project.progress}%</span>
                  </div>
                  <Progress value={project.progress} className="h-2" />
                </div>

                {/* Tasks Summary */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-1">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-gray-600">{completedTasks} de {projectTasks.length} tarefas</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className={`${daysRemaining < 0 ? 'text-red-600' : daysRemaining <= 7 ? 'text-yellow-600' : 'text-gray-600'}`}>
                      {daysRemaining < 0 ? `${Math.abs(daysRemaining)} dias atraso` : 
                       daysRemaining === 0 ? 'Termina hoje' : 
                       `${daysRemaining} dias restantes`}
                    </span>
                  </div>
                </div>

                {/* Team Members */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{projectMembers.length} membros</span>
                  </div>
                  <div className="flex -space-x-2">
                    {projectMembers.slice(0, 3).map((member, index) => (
                      <Avatar key={index} className="w-6 h-6 border-2 border-white">
                        <AvatarImage src={member.avatar} alt={member.name} />
                        <AvatarFallback className="text-xs">{member.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                      </Avatar>
                    ))}
                    {projectMembers.length > 3 && (
                      <div className="w-6 h-6 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center">
                        <span className="text-xs text-gray-600">+{projectMembers.length - 3}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Dates */}
                <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-3 h-3" />
                    <span>Início: {new Date(project.startDate).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-3 h-3" />
                    <span>Fim: {new Date(project.endDate).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-2 pt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => setCurrentProject(project)}
                  >
                    Visualizar
                  </Button>
                  <Button 
                    size="sm" 
                    className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600"
                    onClick={() => setCurrentProject(project)}
                  >
                    Gerenciar
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {projects.length === 0 && (
        <div className="text-center py-12">
          <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum projeto ainda</h3>
          <p className="text-gray-600 mb-6">Crie seu primeiro projeto para começar a organizar suas tarefas</p>
          <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-gradient-to-r from-blue-500 to-purple-600">
            <Plus className="w-4 h-4 mr-2" />
            Criar Primeiro Projeto
          </Button>
        </div>
      )}

      <CreateProjectDialog 
        open={isCreateDialogOpen} 
        onOpenChange={setIsCreateDialogOpen}
      />
    </div>
  );
};